// src/pages/customers/CustomerList.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiPhone,
  FiMapPin,
  FiCreditCard,
} from "react-icons/fi";
import API_BASE_URL from "../../utils/config";

const PAGE_SIZE = 50;

export default function CustomerList() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // debounce search
  const [query, setQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // reset to first page when query changes
  useEffect(() => {
    setPage(1);
  }, [query]);

  const fmtINR = (n) =>
    n == null
      ? "—"
      : Number(n).toLocaleString("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 2,
        });

  const outstandingClass = (v) =>
    Number(v) > 0
      ? "text-rose-700"
      : Number(v) < 0
      ? "text-emerald-700"
      : "text-slate-600";

  const fetchPage = async (p) => {
    try {
      setLoading(true);
      setErr("");
      const res = await axios.get(`${API_BASE_URL}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { q: query, page: p, limit: PAGE_SIZE },
      });
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      console.error(e);
      setErr("Failed to load customers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // load on mount + whenever page/query changes
  useEffect(() => {
    fetchPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [pageCount, page]);

  // delete then refetch current page
  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this customer?");
    if (!ok) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/customers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newTotal = total - 1;
      const newPageCount = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
      const targetPage = Math.min(page, newPageCount);
      setTotal(newTotal);
      setPage(targetPage);
      fetchPage(targetPage);
    } catch (e) {
      console.error(e);
      alert("Delete failed.");
    }
  };

  const formatAddress = (c) =>
    [c.address, c.locality, c.city].filter(Boolean).join(", ");

  const pageStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, total);

  const pageNumbers = useMemo(() => {
    const range = [];
    const delta = 1;
    for (let i = 1; i <= pageCount; i++) {
      if (i === 1 || i === pageCount || (i >= page - delta && i <= page + delta))
        range.push(i);
    }
    const out = [];
    let prev;
    for (const n of range) {
      if (prev) {
        if (n - prev === 2) out.push(prev + 1);
        else if (n - prev > 2) out.push("…");
      }
      out.push(n);
      prev = n;
    }
    return out;
  }, [page, pageCount]);

  const gotoPage = (p) => setPage(Math.min(Math.max(1, p), pageCount));

  return (
    <div className="p-4 md:p-6">
      {/* header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-baseline gap-3">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
            Customers
          </h2>
          <span className="rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-sm font-medium">
            Total: {total.toLocaleString()}
          </span>
        </div>

        {/* desktop add */}
        <Link
          to="/customers/new"
          className="hidden sm:inline-flex items-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm font-semibold shadow-sm"
        >
          <FiPlus /> Add Customer
        </Link>
      </div>

      {/* search */}
      <div className="mb-2">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, GST, DL, phone, or address…"
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            aria-label="Search customers"
          />
        </div>
      </div>

      {/* error */}
      {err && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-rose-50 text-rose-700 px-3 py-2 text-sm border border-rose-200">
          <FiAlertCircle /> {err}
        </div>
      )}

      {/* pager top */}
      <div className="flex items-center justify-between gap-3 py-3 px-2">
        <div className="text-xs sm:text-sm text-slate-600">
          {total > 0 ? (
            <>
              Showing <span className="font-medium">{pageStart}</span>–
              <span className="font-medium">{pageEnd}</span> of{" "}
              <span className="font-medium">{total}</span>
            </>
          ) : (
            <>Showing 0 of 0</>
          )}
        </div>

        {/* Mobile: Prev/Next */}
        <div className="flex sm:hidden items-center gap-1">
          <button
            className="px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(page - 1)}
            disabled={page <= 1}
            title="Previous"
          >
            <FiChevronLeft />
          </button>
          <span className="px-2 text-sm text-slate-600">
            {page} / {pageCount}
          </span>
          <button
            className="px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(page + 1)}
            disabled={page >= pageCount}
            title="Next"
          >
            <FiChevronRight />
          </button>
        </div>

        {/* Desktop: full pager */}
        <div className="hidden sm:flex items-center gap-1">
          <button
            className="px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(1)}
            disabled={page <= 1}
            title="First"
          >
            <FiChevronsLeft />
          </button>
          <button
            className="px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(page - 1)}
            disabled={page <= 1}
            title="Previous"
          >
            <FiChevronLeft />
          </button>
          {pageNumbers.map((n, idx) =>
            n === "…" ? (
              <span key={`dots-${idx}`} className="px-2 text-slate-400">
                …
              </span>
            ) : (
              <button
                key={n}
                onClick={() => gotoPage(n)}
                className={`min-w-[2.25rem] px-2 py-1 rounded-md border text-sm ${
                  n === page
                    ? "border-blue-600 text-blue-700 bg-blue-50 font-semibold"
                    : "border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
              >
                {n}
              </button>
            )
          )}
          <button
            className="px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(page + 1)}
            disabled={page >= pageCount}
            title="Next"
          >
            <FiChevronRight />
          </button>
          <button
            className="px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(pageCount)}
            disabled={page >= pageCount}
            title="Last"
          >
            <FiChevronsRight />
          </button>
        </div>
      </div>

      {/* ========= MOBILE: card list (match ProductList) ========= */}
      <div className="sm:hidden space-y-3">
        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-500">
            Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-500">
            No matching customers found
          </div>
        )}
        {!loading &&
          rows.map((c) => (
            <div
              key={c.id}
              className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {c.name || "—"}
                  </div>

                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                      Customer
                    </span>
                    {c.gst_no && (
                      <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                        GST
                      </span>
                    )}
                    {c.dl_no && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                        DL
                      </span>
                    )}
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-3 text-[13px] text-slate-700">
                    {c.phone && (
                      <span className="inline-flex items-center gap-1">
                        <FiPhone /> {c.phone}
                      </span>
                    )}
                    {c.gst_no && (
                      <span className="inline-flex items-center gap-1">
                        <FiCreditCard /> {c.gst_no}
                      </span>
                    )}
                  </div>

                  <div className="mt-1 flex items-start gap-1 text-xs text-slate-600">
                    <FiMapPin className="mt-0.5 shrink-0" />
                    <span className="line-clamp-2">
                      {formatAddress(c) || "—"}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <div
                    className={`text-sm font-semibold ${outstandingClass(
                      c.outstanding
                    )}`}
                    title="Invoices − Payments − Credit Notes"
                  >
                    {fmtINR(c.outstanding)}
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  onClick={() => navigate(`/customers/${c.id}`)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium"
                >
                  <FiEye /> View
                </button>
                <Link
                  to={`/customers/edit/${c.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-amber-300 text-amber-700 text-sm hover:bg-amber-50"
                >
                  <FiEdit2 /> Edit
                </Link>
                <button
                  onClick={() => handleDelete(c.id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 text-sm hover:bg-rose-50"
                >
                  <FiTrash2 /> Delete
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* ========= DESKTOP/TABLET: table ========= */}
      <div className="hidden sm:block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 sticky top-0 z-10 text-slate-600">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">GST</th>
                <th className="px-4 py-3 font-medium">DL</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">Address</th>
                <th className="px-4 py-3 font-medium text-right">Outstanding</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-slate-50">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No matching customers found
                  </td>
                </tr>
              )}

              {!loading &&
                rows.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/70">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {c.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{c.gst_no || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{c.dl_no || "—"}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {c.phone || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-[520px] truncate">
                      {formatAddress(c) || "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`${outstandingClass(
                          c.outstanding
                        )} font-semibold`}
                        title="Invoices − Payments − Credit Notes"
                      >
                        {fmtINR(c.outstanding)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/customers/${c.id}`)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-blue-700 hover:bg-blue-50"
                        >
                          <FiEye /> View
                        </button>
                        <Link
                          to={`/customers/edit/${c.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-amber-700 hover:bg-amber-50"
                        >
                          <FiEdit2 /> Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-rose-700 hover:bg-rose-50"
                        >
                          <FiTrash2 /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* pager bottom */}
      <div className="flex items-center justify-between gap-3 py-3 px-2">
        <div className="text-xs sm:text-sm text-slate-600">
          {total > 0 ? (
            <>
              Showing <span className="font-medium">{pageStart}</span>–
              <span className="font-medium">{pageEnd}</span> of{" "}
              <span className="font-medium">{total}</span>
            </>
          ) : (
            <>Showing 0 of 0</>
          )}
        </div>

        {/* Mobile: Prev/Next */}
        <div className="flex sm:hidden items-center gap-1">
          <button
            className="px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(page - 1)}
            disabled={page <= 1}
            title="Previous"
          >
            <FiChevronLeft />
          </button>
          <span className="px-2 text-sm text-slate-600">
            {page} / {pageCount}
          </span>
          <button
            className="px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(page + 1)}
            disabled={page >= pageCount}
            title="Next"
          >
            <FiChevronRight />
          </button>
        </div>

        {/* Desktop: full pager */}
        <div className="hidden sm:flex items-center gap-1">
          <button
            className="px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(1)}
            disabled={page <= 1}
            title="First"
          >
            <FiChevronsLeft />
          </button>
          <button
            className="px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(page - 1)}
            disabled={page <= 1}
            title="Previous"
          >
            <FiChevronLeft />
          </button>
          {pageNumbers.map((n, idx) =>
            n === "…" ? (
              <span key={`dotsb-${idx}`} className="px-2 text-slate-400">
                …
              </span>
            ) : (
              <button
                key={n}
                onClick={() => gotoPage(n)}
                className={`min-w-[2.25rem] px-2 py-1 rounded-md border text-sm ${
                  n === page
                    ? "border-blue-600 text-blue-700 bg-blue-50 font-semibold"
                    : "border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
              >
                {n}
              </button>
            )
          )}
          <button
            className="px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(page + 1)}
            disabled={page >= pageCount}
            title="Next"
          >
            <FiChevronRight />
          </button>
          <button
            className="px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => gotoPage(pageCount)}
            disabled={page >= pageCount}
            title="Last"
          >
            <FiChevronsRight />
          </button>
        </div>
      </div>

      {/* MOBILE FAB: + Add */}
      <Link
        to="/customers/new"
        className="fixed bottom-5 right-5 h-12 w-12 rounded-full bg-green-600 text-white shadow-lg shadow-green-600/30 active:scale-95 sm:hidden grid place-items-center"
        aria-label="Add customer"
      >
        <FiPlus className="h-5 w-5" />
      </Link>
    </div>
  );
}
